import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, delay } from 'rxjs/operators';
import { PaymentConfigItem } from '../payment-config.config';

// ============================================================================
// INTERFACES
// ============================================================================

export interface PaymentConfigSearchParams {
  partnerName?: string;
  methodName?: string;
  storeId?: string;
  isDisable?: boolean;
  test?: boolean;
  createdDateStart?: string;
  createdDateEnd?: string;
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface PaymentConfigResponse {
  data: PaymentConfigItem[];
  totalRecords: number;
  page: number;
  size: number;
  totalPages: number;
}

export interface PaymentConfigCreateRequest {
  partner_name: string;
  partner_code: string;
  method_name: string;
  store_Id: string;
  access_key: string;
  secret_key: string;
  isDisable: boolean;
  test: boolean;
}

export interface PaymentConfigUpdateRequest extends PaymentConfigCreateRequest {
  access_key: string; // For identifying the record to update
}

// ============================================================================
// MOCK DATA
// ============================================================================

const MOCK_PAYMENT_CONFIGS: PaymentConfigItem[] = [
  {
    access_key: 'cV7mjOLAWlcuDDot',
    isDisable: false,
    method_name: 'momo',
    partner_code: 'MOMOBPVF20220126',
    partner_name: 'KienStores',
    secret_key: '0JLAly0hSJaxzefvQihMcjh6L6qqOB3V',
    store_Id: 'store001',
    test: true,
    createdDate: '2024-01-15T10:30:00Z',
    updatedDate: '2024-01-15T10:30:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    access_key: 'dX8nkPMBXmduEEpu',
    isDisable: true,
    method_name: 'vnpay',
    partner_code: 'VNP20240115',
    partner_name: 'TechCorp',
    secret_key: '1KMBmz1iTKbyafgwRjiNdkj7M7rrPC4W',
    store_Id: 'store002',
    test: false,
    createdDate: '2024-01-14T14:20:00Z',
    updatedDate: '2024-01-16T09:15:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    access_key: 'eY9olQNCYnevFFqv',
    isDisable: false,
    method_name: 'paypal',
    partner_code: 'PP20240113',
    partner_name: 'GlobalPay',
    secret_key: '2LNCn02jULczbghxSkjOelj8N8ssQD5X',
    store_Id: 'store003',
    test: true,
    createdDate: '2024-01-13T16:45:00Z',
    updatedDate: '2024-01-13T16:45:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    access_key: 'fZ0pmRODZofwGGrw',
    isDisable: false,
    method_name: 'stripe',
    partner_code: 'STR20240112',
    partner_name: 'StripeCorp',
    secret_key: '3MODo13kVMdachiYlkjPflj9O9ttRE6Y',
    store_Id: 'store004',
    test: false,
    createdDate: '2024-01-12T11:30:00Z',
    updatedDate: '2024-01-12T11:30:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
  {
    access_key: 'gA1qnSPEapgxHHsx',
    isDisable: true,
    method_name: 'bank_transfer',
    partner_code: 'BT20240111',
    partner_name: 'BankDirect',
    secret_key: '4NPep24lWNedbdiZmkjQglj0P0uuSF7Z',
    store_Id: 'store005',
    test: true,
    createdDate: '2024-01-11T08:15:00Z',
    updatedDate: '2024-01-17T13:20:00Z',
    createdBy: 'admin',
    updatedBy: 'admin',
  },
];

// ============================================================================
// SERVICE
// ============================================================================

@Injectable({
  providedIn: 'root',
})
export class PaymentConfigService {
  //   private readonly baseUrl = `${environment.apiUrl}/configuration/finance/payment-configuration`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Get payment configurations with search and pagination
   */
  getPaymentConfigs(
    params: PaymentConfigSearchParams = {}
  ): Observable<PaymentConfigResponse> {
    // For now, return mock data with simulated API delay
    return of(this.filterMockData(params)).pipe(
      delay(500), // Simulate network delay
      map((filteredData) => {
        const page = params.page || 1;
        const size = params.size || 10;
        const startIndex = (page - 1) * size;
        const endIndex = startIndex + size;

        const paginatedData = filteredData.slice(startIndex, endIndex);

        return {
          data: paginatedData,
          totalRecords: filteredData.length,
          page,
          size,
          totalPages: Math.ceil(filteredData.length / size),
        };
      })
    );

    // TODO: Replace with actual API call when backend is ready
    // const httpParams = this.buildHttpParams(params);
    // return this.http.get<PaymentConfigResponse>(this.baseUrl, { params: httpParams });
  }

  /**
   * Get payment configuration by access key
   */
  getPaymentConfig(accessKey: string): Observable<PaymentConfigItem> {
    // Mock implementation
    const config = MOCK_PAYMENT_CONFIGS.find(
      (item) => item.access_key === accessKey
    );
    if (!config) {
      throw new Error('Payment configuration not found');
    }
    return of(config).pipe(delay(300));

    // TODO: Replace with actual API call
    // return this.http.get<PaymentConfigItem>(`${this.baseUrl}/${accessKey}`);
  }

  /**
   * Create new payment configuration
   */
  createPaymentConfig(
    data: PaymentConfigCreateRequest
  ): Observable<PaymentConfigItem> {
    // Mock implementation
    const newConfig: PaymentConfigItem = {
      ...data,
      createdDate: new Date().toISOString(),
      updatedDate: new Date().toISOString(),
      createdBy: 'current_user',
      updatedBy: 'current_user',
    };

    MOCK_PAYMENT_CONFIGS.unshift(newConfig);
    return of(newConfig).pipe(delay(500));

    // TODO: Replace with actual API call
    // return this.http.post<PaymentConfigItem>(this.baseUrl, data);
  }

  /**
   * Update payment configuration
   */
  updatePaymentConfig(
    accessKey: string,
    data: PaymentConfigUpdateRequest
  ): Observable<PaymentConfigItem> {
    // Mock implementation
    const index = MOCK_PAYMENT_CONFIGS.findIndex(
      (item) => item.access_key === accessKey
    );
    if (index === -1) {
      throw new Error('Payment configuration not found');
    }

    const updatedConfig: PaymentConfigItem = {
      ...MOCK_PAYMENT_CONFIGS[index],
      ...data,
      updatedDate: new Date().toISOString(),
      updatedBy: 'current_user',
    };

    MOCK_PAYMENT_CONFIGS[index] = updatedConfig;
    return of(updatedConfig).pipe(delay(500));

    // TODO: Replace with actual API call
    // return this.http.put<PaymentConfigItem>(`${this.baseUrl}/${accessKey}`, data);
  }

  /**
   * Delete payment configurations
   */
  deletePaymentConfigs(accessKeys: string[]): Observable<void> {
    // Mock implementation
    accessKeys.forEach((accessKey) => {
      const index = MOCK_PAYMENT_CONFIGS.findIndex(
        (item) => item.access_key === accessKey
      );
      if (index !== -1) {
        MOCK_PAYMENT_CONFIGS.splice(index, 1);
      }
    });

    return of(void 0).pipe(delay(300));

    // TODO: Replace with actual API call
    // return this.http.delete<void>(`${this.baseUrl}/bulk`, {
    //   body: { accessKeys }
    // });
  }

  /**
   * Delete single payment configuration
   */
  deletePaymentConfig(accessKey: string): Observable<void> {
    return this.deletePaymentConfigs([accessKey]);
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /**
   * Filter mock data based on search parameters
   */
  private filterMockData(
    params: PaymentConfigSearchParams
  ): PaymentConfigItem[] {
    let filteredData = [...MOCK_PAYMENT_CONFIGS];

    // Filter by partner name
    if (params.partnerName) {
      filteredData = filteredData.filter((item) =>
        item.partner_name
          .toLowerCase()
          .includes(params.partnerName!.toLowerCase())
      );
    }

    // Filter by method name
    if (params.methodName) {
      filteredData = filteredData.filter(
        (item) => item.method_name === params.methodName
      );
    }

    // Filter by store ID
    if (params.storeId) {
      filteredData = filteredData.filter((item) =>
        item.store_Id.toLowerCase().includes(params.storeId!.toLowerCase())
      );
    }

    // Filter by status
    if (params.isDisable !== undefined) {
      filteredData = filteredData.filter(
        (item) => item.isDisable === params.isDisable
      );
    }

    // Filter by test mode
    if (params.test !== undefined) {
      filteredData = filteredData.filter((item) => item.test === params.test);
    }

    // Filter by date range
    if (params.createdDateStart) {
      filteredData = filteredData.filter(
        (item) =>
          item.createdDate &&
          new Date(item.createdDate) >= new Date(params.createdDateStart!)
      );
    }

    if (params.createdDateEnd) {
      filteredData = filteredData.filter(
        (item) =>
          item.createdDate &&
          new Date(item.createdDate) <= new Date(params.createdDateEnd!)
      );
    }

    // Sort data
    if (params.sortBy) {
      filteredData.sort((a, b) => {
        const aValue = (a as any)[params.sortBy!];
        const bValue = (b as any)[params.sortBy!];

        if (aValue < bValue) return params.sortDirection === 'desc' ? 1 : -1;
        if (aValue > bValue) return params.sortDirection === 'desc' ? -1 : 1;
        return 0;
      });
    }

    return filteredData;
  }

  /**
   * Build HTTP parameters for API calls
   */
  private buildHttpParams(params: PaymentConfigSearchParams): HttpParams {
    let httpParams = new HttpParams();

    Object.keys(params).forEach((key) => {
      const value = (params as any)[key];
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, value.toString());
      }
    });

    return httpParams;
  }
}
